import { useEffect, useState } from 'react';
import { Select, type SelectProps, type ComboboxItem } from '@mantine/core';
import {
  TIMEZONE_SELECT_DATA,
  getTimezoneLabel,
  isOfferedTimezone,
  stripOffsetSuffix,
  timezoneSearchTerms,
  toSelectValue,
} from '../../config/timezones';

export interface TimezoneSelectProps extends Omit<
  SelectProps,
  'data' | 'searchValue' | 'onSearchChange' | 'filter'
> {
  /**
   * Options to place above the timezone groups, for filter selects that need a
   * sentinel such as `{ value: 'all', label: 'All Timezones' }`. These are
   * never normalized as IANA identifiers.
   */
  extraOptions?: ComboboxItem[];
}

/**
 * Timezone picker backed by one option per distinct clock.
 *
 * Two behaviours live here so they are defined once rather than at each call
 * site.
 *
 * First, the search text is cleared when the dropdown opens and the label is
 * restored when it closes. A plain `<Select searchable />` keeps the selected
 * option's label in the input, so an admin who clicks and types immediately
 * gets "No timezone found".
 *
 * Second, search matches the hidden city aliases of every zone that collapsed
 * into an option. Labels name the zone rather than a city, so "Eastern Time"
 * appears once instead of fifteen times, and a Toronto coach can still type
 * "toronto" to find it.
 */
export function TimezoneSelect({
  value,
  extraOptions,
  ...rest
}: TimezoneSelectProps) {
  const isExtra = extraOptions?.some(o => o.value === value) ?? false;
  const resolved = isExtra ? (value as string) : toSelectValue(value);
  const label = isExtra
    ? (extraOptions?.find(o => o.value === value)?.label ?? '')
    : resolved
      ? getTimezoneLabel(resolved)
      : '';

  const [search, setSearch] = useState(label);

  // Keep the input text in step when the value changes from outside, for
  // example a form reset or a different row being edited.
  useEffect(() => setSearch(label), [label]);

  // A row saved before the offered list was shortened may hold a zone that is
  // no longer a choice, such as Asia/Tokyo. Mantine renders a value with no
  // matching option as an empty field, which reads as data loss, so append the
  // current one. It is the only way to reach that zone, so shortening the list
  // stops it being picked without erasing what is already set.
  const keepsCurrent =
    Boolean(resolved) && !isExtra && !isOfferedTimezone(resolved);
  const current = keepsCurrent
    ? [{ value: resolved as string, label: `${label} (currently set)` }]
    : [];

  const data = [...(extraOptions ?? []), ...TIMEZONE_SELECT_DATA, ...current];

  return (
    <Select
      {...rest}
      data={data}
      value={resolved}
      searchable
      nothingFoundMessage="No timezone found"
      searchValue={search}
      onSearchChange={setSearch}
      onDropdownOpen={() => setSearch('')}
      onDropdownClose={() => setSearch(label)}
      filter={({ options, search: term }) => {
        const q = term.trim().toLowerCase();
        if (!q) return options;
        const matches = (item: ComboboxItem) =>
          timezoneSearchTerms(item.value).includes(q) ||
          // The label fallback covers the sentinel options a caller passes in,
          // such as "All Timezones", which are not IANA identifiers. Strip the
          // offset for the same reason timezoneSearchTerms does.
          stripOffsetSuffix(item.label).toLowerCase().includes(q);
        return options
          .map(entry =>
            'group' in entry
              ? { ...entry, items: entry.items.filter(matches) }
              : entry
          )
          .filter(entry =>
            'group' in entry ? entry.items.length > 0 : matches(entry)
          );
      }}
    />
  );
}
